import React, { useState } from 'react'
import { Card, Grid, makeStyles, Typography, Paper, Divider } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { organizationFormSchema } from 'schemas/sponsor'
import CustomTextField from 'components/Form/CustomTextField'
import { SPONSOR_INFO_TEXT, SubjectInfoMessages } from 'constants/index'
import { Box } from '@mui/material'
import ProgressButton from 'components/ProgressButton'
import { useDispatch } from 'react-redux'
import { verifyGoogleRecaptcha } from 'store/auth'
import { toast } from 'utils/notify'
import Recaptcha from 'components/Recaptcha'
import SubjectSpecificDetails from 'components/SubjectSpecificDetails'
import ImageSelector from 'components/ImageSelector'
import InfoIconAlongText from 'components/InfoIconAlongText'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import ErrorMessage from 'components/ErrorMessage'
import { colors } from 'utils/colors'

const PublicOrganizationForm = (props) => {
  const { organization, handleSubmit, submitTitle } = props
  const dispatch = useDispatch()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [recaptchaCode, setRecaptchaCode] = useState('')
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false)
  const [logoFile, setLogoFile] = useState(null)
  const [logoDeleted, setLogoDeleted] = useState(false)
  const [adFile, setAdFile] = useState(null)
  const [adDeleted, setAdDeleted] = useState(false)
  const [brochureFile, setBrochureFile] = useState(null)
  const [brochureDeleted, setBrochureDeleted] = useState(false)

  const handleChangeRecaptcha = (value) => {
    setRecaptchaCode(value)
    if (value) {
      setIsRecaptchaValid(true)
    }
  }
  const handleExpired = () => {
    setIsRecaptchaValid(false)
  }

  const initialValues = {
    organization: organization?.organization ?? '',
    booth: organization?.booth ?? '',
    websiteUrl: organization?.websiteUrl ?? '',
    introduction: organization?.introduction ?? '',
    message: organization?.message ?? '',
    bannerClickUrl: organization?.bannerClickUrl ?? ''
  }

  const handleLogoChange = (file) => {
    setLogoFile(file)
    setLogoDeleted(false)
  }

  const handleLogoRemove = () => {
    setLogoFile(null)
    setLogoDeleted(true)
  }

  const handleAdChange = (file) => {
    setAdFile(file)
    setAdDeleted(false)
  }

  const handleAdRemove = () => {
    setAdFile(null)
    setAdDeleted(true)
  }

  const handleBrochureChange = (file) => {
    setBrochureFile(file)
    setBrochureDeleted(false)
  }

  const handleBrochureRemove = () => {
    setBrochureFile(null)
    setBrochureDeleted(true)
  }

  return (
    <Card className={classes.mainCard} elevation={3}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={organizationFormSchema({ showSponsorshipLevel: false })}
        onSubmit={async (values) => {
          try {
            setLoading(true)
            const isVerified = await dispatch(verifyGoogleRecaptcha(recaptchaCode))
            if (isVerified) {
              await handleSubmit({
                ...values,
                logoImageName: logoFile,
                logoImageNameDeleted: logoDeleted,
                adImageName: adFile,
                adImageNameDeleted: adDeleted,
                brochureImageName: brochureFile,
                brochureImageNameDeleted: brochureDeleted
              })
            } else {
              toast.error('reCAPTCHA verification failed. Please try again.')
            }
            setLoading(false)
          } catch (e) {
            // do nothing
          } finally {
            setLoading(false) // do nothing but stops the loading
          }
        }}>
        {({ setFieldValue, errors, values, touched }) => (
          <Form>
            <Box sx={{ pb: 1 }}>
              <Typography variant="h4" className={classes.pageTitle}>
                Register as an Organization
              </Typography>
            </Box>

            <Paper className={classes.detailsPaper} elevation={1}>
              <SubjectSpecificDetails infoText={SubjectInfoMessages.SPONSOR} />
            </Paper>

            <Paper className={classes.formPaper} elevation={1}>
              <Typography variant="h6" className={classes.formSectionTitle}>
                Basic Information
              </Typography>
              <Divider className={classes.sectionDivider} />

              <Grid container spacing={3} className={classes.form}>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Organization Name"
                    name="organization"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Booth No"
                    name="booth"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Website URL"
                    name="websiteUrl"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                {organization?.sponsorshipLevel?.allowAdBanner && (
                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      label="Banner Click URL"
                      name="bannerClickUrl"
                      type="text"
                      infoText={SPONSOR_INFO_TEXT.bannerClickURL}
                      className={classes.textField}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            <Paper className={classes.formPaper} elevation={1}>
              <Typography variant="h6" className={classes.formSectionTitle}>
                Information & Content
              </Typography>
              <Divider className={classes.sectionDivider} />

              <Grid container spacing={3} className={classes.form}>
                <Grid item xs={12}>
                  <Box className={classes.editorContainer}>
                    <label className={classes.labelA}>
                      Introduction
                      <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.introduction} />
                    </label>
                    <Box className={classes.quillWrapper}>
                      <TipTapEditor
                        value={values.introduction}
                        onChange={(value) => setFieldValue('introduction', value)}
                        placeholder="Write introduction here..."
                        error={!!(touched?.introduction && errors?.introduction)}
                        minHeight="150px"
                        maxHeight="400px"
                        hasPlaceholders={false}
                      />
                    </Box>
                    <ErrorMessage message={touched?.introduction && errors?.introduction} />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className={classes.editorContainer}>
                    <label className={classes.labelA}>
                      Message
                      <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.message} />
                    </label>
                    <Box className={classes.quillWrapper}>
                      <TipTapEditor
                        value={values.message}
                        onChange={(value) => setFieldValue('message', value)}
                        placeholder="Write message here..."
                        error={!!(touched?.message && errors?.message)}
                        minHeight="150px"
                        maxHeight="400px"
                        hasPlaceholders={false}
                      />
                    </Box>
                    <ErrorMessage message={touched?.message && errors?.message} />
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {(organization?.sponsorshipLevel?.allowLogo ||
              organization?.sponsorshipLevel?.allowAdBanner ||
              organization?.sponsorshipLevel?.allowBrochure) && (
              <Paper className={classes.formPaper} elevation={1}>
                <Typography variant="h6" className={classes.formSectionTitle}>
                  Organization Branding
                </Typography>
                <Divider className={classes.sectionDivider} />

                <Grid container spacing={3} className={classes.form}>
                  {organization?.sponsorshipLevel?.allowLogo && (
                    <Grid item xs={12} md={6}>
                      <Box className={classes.imageContainer}>
                        <ImageSelector
                          title="Logo"
                          subTitle="Allowed Format: PNG/JPG/JPEG; Dimensions : 320x320"
                          infoText={SPONSOR_INFO_TEXT.logo}
                          onChange={handleLogoChange}
                          onRemove={handleLogoRemove}
                          initialImage={organization?.imageUrls?.logoImageNameUrl || null}
                        />
                      </Box>
                    </Grid>
                  )}
                  {organization?.sponsorshipLevel?.allowAdBanner && (
                    <Grid item xs={12} md={6}>
                      <Box className={classes.imageContainer}>
                        <ImageSelector
                          title="Ad Banner"
                          subTitle="Allowed Format: PNG/JPG/JPEG; Recommended Dimensions : 300x100"
                          infoText={SPONSOR_INFO_TEXT.adBanner}
                          onChange={handleAdChange}
                          onRemove={handleAdRemove}
                          initialImage={organization?.imageUrls?.adImageNameUrl || null}
                        />
                      </Box>
                    </Grid>
                  )}
                  {organization?.sponsorshipLevel?.allowBrochure && (
                    <Grid item xs={12} md={6}>
                      <Box className={classes.imageContainer}>
                        <ImageSelector
                          title="Brochure"
                          subTitle="Allowed Format: PNG/JPG/JPEG; Dimensions : 300x400"
                          infoText={SPONSOR_INFO_TEXT.brochure}
                          onChange={handleBrochureChange}
                          onRemove={handleBrochureRemove}
                          initialImage={organization?.imageUrls?.brochureImageNameUrl || null}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            <Box className={classes.buttonBox}>
              <Recaptcha handleChange={handleChangeRecaptcha} onExpired={handleExpired} />
              <ProgressButton
                type="submit"
                label={submitTitle}
                className={classes.submitButton}
                inProgress={loading}
                disabled={loading || !isRecaptchaValid}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  )
}

export default PublicOrganizationForm

const useStyles = makeStyles({
  mainCard: {
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    overflow: 'hidden',
    transition: 'all 0.3s ease-in-out'
  },
  pageTitle: {
    fontWeight: 600,
    color: colors.indigo,
    marginBottom: '0.5rem',
    fontSize: '1.75rem'
  },
  detailsPaper: {
    marginBottom: '2rem',
    borderRadius: '12px'
  },
  formPaper: {
    marginBottom: '2rem',
    borderRadius: '12px',
    padding: '1.5rem',
    transition: 'all 0.3s ease'
  },
  formSectionTitle: {
    color: colors.indigo,
    fontWeight: 500,
    marginBottom: '0.75rem',
    fontSize: '1.25rem'
  },
  sectionDivider: {
    marginBottom: '1.5rem',
    backgroundColor: colors.lightIndigo,
    height: '2px'
  },
  form: {
    marginTop: '1rem'
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.indigo
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.indigo,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: colors.indigo
    }
  },
  labelA: {
    fontSize: '14px',
    letterSpacing: '0.3px',
    color: colors.indigo,
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '0.5rem',
    fontWeight: 500
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  quillWrapper: {
    marginBottom: '0.5rem'
  },
  imageContainer: {
    marginTop: '0.5rem'
  },
  buttonBox: {
    marginTop: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem'
  },
  submitButton: {
    borderRadius: '8px',
    boxShadow: '0 4px 10px rgba(21, 180, 219, 0.25)',
    textTransform: 'none',
    fontWeight: 500,
    padding: '0.5rem 1.5rem',
    backgroundColor: colors.indigo
  },
  cancelButton: {
    borderRadius: '8px',
    textTransform: 'none',
    padding: '0.5rem 1.5rem',
    color: colors.grey
  },
  singleInfo: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center'
  },
  label: {
    minWidth: '150px',
    fontSize: '15px',
    fontWeight: '600',
    color: colors.indigo
  }
})
